import { AxisTickProps } from '@nivo/axes';
import { classes, Colors } from '@whoop/web-components';
import { getHourFromEpoch } from 'dataVisualizations/utils/utils';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { getMonthDayValues } from 'progress/utils/date';
import { MonthDayValues } from 'types/analytics';
import { WidgetType } from 'insights/components/widget/widget';
import { DAILY_GRAPH_LABEL_COLOR, FORMAT_METRIC_VALUE } from 'insights/components/widget/widgetFormatter';
import styles from './axis.module.scss';
import { getAxisTickColor } from './utils';

// Needed to import this way so that there weren't type errors with jest testing
dayjs.extend(duration);

export function labelXAxis(
  tick: AxisTickProps<string>,
  gridXValues: number[],
) {
  const {
    x, y, value,
  } = tick;

  if (gridXValues.includes(Number(value))) {
    const hourFromEpoch = getHourFromEpoch(Number(value));
    const axisOffset = `translate(${x},${y + 18})`;
    return (
      <g transform={axisOffset}>
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          className={styles.durationAxis}
          fill={Colors.gray700}
        >
          <tspan x="0">
            {hourFromEpoch.hour}
          </tspan>
          <tspan x="0" dy="18px">
            {hourFromEpoch.amPm}
          </tspan>
        </text>
      </g>
    );
  }
  return <g />;
}

export function DayYTick(
  x: number,
  y: number,
  value: number,
  tickFormatter?: (val: number) => string,
  className?: string,
) {
  return (
    <g transform={`translate(${x - 20},${y})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        className={classes(styles.yAxisTick, className)}
        fill={Colors.blackAlpha500}
      >
        <tspan>
          {tickFormatter ? tickFormatter(value) : value}
        </tspan>
      </text>
    </g>
  );
}

export function DayMonthXAxisTick(
  {
    tick,
    numOfSelectedDays,
    className,
  }:
  {
    tick: { x: number; y: number; value: string; tickIndex: number };
    numOfSelectedDays: number;
    className?: string;
  },
) {
  const {
    x, y, value, tickIndex,
  } = tick;
  const { day, month, dayNum }: MonthDayValues = getMonthDayValues(value);
  let firstLine = '';
  let secondLine = '';

  if (numOfSelectedDays <= 14) {
    firstLine = day;
    secondLine = dayNum.toString();
  } else if (numOfSelectedDays >= 15 && numOfSelectedDays <= 90) {
    if (tickIndex % 7 !== 0) {
      return <g />;
    }
    firstLine = month;
    secondLine = dayNum.toString();
  } else {
    if (dayNum !== 1) {
      return <g />;
    }
    firstLine = month;
    secondLine = '';
  }

  return (
    <g transform={`translate(${x},${y + 14})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        className={classes(styles.dayMonthAxisTick, className)}
        fill="#808080"
      >
        <tspan x="0" dy="4px">
          {firstLine}
        </tspan>
        <tspan x="0" dy="18px">
          {secondLine}
        </tspan>
      </text>
    </g>
  );
}

export function RawHeartRateAxis(tick: AxisTickProps<string>) {
  const { x, y, value } = tick;
  // 6 is ~ the pixel width of one digit, so we multiply the number of digits in the axis value
  // The 16 and 5 are just offset values that position the value where we want it to be displayed
  const axisOffset = `translate(${x - (16 + (value.toString().length * 6))},${y + 5})`;

  return (
    <g fill={Colors.gray700} transform={axisOffset}>
      <text className={styles.heartRateAxis}>
        {value}
      </text>
    </g>
  );
}

/*
  Zones of max bar graph axes utils
 */

export function CustomZoneTick(x: number, y: number, index: number, zoneTickLabels: string[]) {
  return (
    <g transform={`translate(${x + 64},${y})`}>
      <text
        textAnchor="end"
        dominantBaseline="middle"
        className={styles.tick}
        fill={Colors.blackAlpha300}
      >
        {zoneTickLabels[index]}
      </text>
    </g>
  );
}

export function CustomHorizontalBarTick(
  x: number,
  y: number,
  val: number,
  tickFormatter: (val: number) => string,
) {
  return (
    <g transform={`translate(${x + 32},${y + 2})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        className={styles.tick}
      >
        <tspan>
          {tickFormatter(val)}
        </tspan>
      </text>
    </g>
  );
}

/* in order to add more padding to the y-axis from the datapoints we need to
1) position the axis label away from the graph
2) extend the grid line from the axis label to the graph
so it doesn't look like the label is floating in space
The grid lines themselves cannot be extended to the axis so we need to create a line that will
extend from the axis label back to the grid line
*/
export function StrainRecoveryTick(
  x: number,
  y: number,
  value: string,
  strain: boolean,
) {
  const STRAIN_MAX = 21;
  const percentage = Number(((Number(value) / STRAIN_MAX) * 100).toFixed());

  // setting the x value depending on strain or recovery axis
  let transformXVal = 0;
  // if extendedGridLine is true
  // we want to position the x value of the axis label further away from the graph
  // recovery axis on the right side
  transformXVal = !strain ? x + 76 : x - 16;
  // strain axis on the left side
  transformXVal = strain ? transformXVal - 60 : transformXVal;

  return (
    <g transform={`translate(${transformXVal},${y})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        className={styles.strainRecoveryTick}
        fill={getAxisTickColor(strain, percentage)}
      >
        {strain ? value : `${percentage}%`}
      </text>
      {strain
        // x1 = 0 would be at at the center of the text
        // left axis so we move in the positive direction
        ? <line opacity="1" x1={20} x2={76} y1={0} y2={0} stroke="#dddddd" strokeWidth=".5" />
        // right axis so we move in the negative direction
        : <line opacity="1" x1={-20} x2={-76} y1={0} y2={0} stroke="#dddddd" strokeWidth=".5" />}
    </g>
  );
}

const renderText = (x: number, y: number, color: string, value: string) => (
  <g transform={`translate(${x},${y})`}>
    <text
      textAnchor="middle"
      dominantBaseline="middle"
      className={styles.insightsTick}
      fill={color}
    >
      {value}
    </text>
  </g>
);

export function InsightsYAxisTick(
  x: number,
  y: number,
  value: string,
  widgetType: WidgetType,
) {
  const showPercent = [WidgetType.RECOVERY, WidgetType.SLEEP_PERFORMANCE].includes(widgetType);
  const color = DAILY_GRAPH_LABEL_COLOR[widgetType](Number(value));

  // Offset label x value. Add 5px if we're showing the percent sign for more space.
  const transformXVal = x - 15 - (showPercent ? 5 : 0);

  return renderText(transformXVal, y, color, FORMAT_METRIC_VALUE[widgetType](Number(value)));
}

export function TrainingAdaptationYAxisTick(
  x: number,
  y: number,
  value: string,
) {
  const transformXVal = x - 15;

  return renderText(
    transformXVal,
    y,
    Colors.whiteAlpha500,
    FORMAT_METRIC_VALUE[WidgetType.DAY_STRAIN](Number(value)),
  );
}

export function TrainingAdaptationXAxisTick(
  x: number,
  y: number,
  value: string,
) {
  const transformYVal = y + 15;

  return renderText(
    x,
    transformYVal,
    Colors.whiteAlpha500,
    FORMAT_METRIC_VALUE[WidgetType.RECOVERY](Number(value)),
  );
}

export function ProfileAxisTick(
  x: number,
  y: number,
  value: string,
  profileMap: Map<number, string>,
  highlightedIndex: string,
  setHighlightedIndex: (highlightedUser: string) => void,
) {
  const addOpacity = highlightedIndex && value !== highlightedIndex;
  const handleMouseEnter = () => {
    setHighlightedIndex(value);
  };

  const handleMouseLeave = () => {
    setHighlightedIndex(null);
  };

  return (
    <g
      transform={`translate(${x - 12},${y + 10})`}
      onMouseOver={handleMouseEnter}
      onMouseOut={handleMouseLeave}
      data-testid="profileAxisComponent"
    >
      <foreignObject x="0" y="0" width="24px" height="24px">
        <img
          alt="User Profile"
          className={styles.avatar}
          style={{ opacity: addOpacity ? '20%' : '100%' }}
          src={profileMap.get(Number(value))}
        />
      </foreignObject>
    </g>
  );
}
